import { useEffect, useRef } from 'react'
import { BrowserRouter, Route, Routes, useLocation, useNavigate, useParams } from 'react-router'
import { Provider } from 'mobx-react'
import { getEnv } from 'mobx-state-tree'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isBetween from 'dayjs/plugin/isBetween'
import relativeTime from 'dayjs/plugin/relativeTime'
import './index.css'
import 'tippy.js/themes/light.css'

import AppStore from './AppStore'
import Guide from './views/guide'
import { LoginDashboard } from './views'
import { RetailDashboard } from './views'
import { ScrollToTop } from './components'
import ToastBar from './components/ToastBar'
import AppGateway from './AppGateway'
import ThemeWrapper from './themes/ThemeWrapper'
import AtmosLogin from './views/login/AtmosLogin'

// Add dayjs plugins needed across application
dayjs.extend(isSameOrBefore).extend(isSameOrAfter).extend(isBetween).extend(relativeTime)

function AppContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const storeRef = useRef()

  if (!storeRef.current) {
    storeRef.current = AppStore.create({}, {})
  }
  const store = storeRef.current

  useEffect(() => {
    const env = getEnv(store)
    env.navigate = navigate
    env.location = location
    env.match = { params }
  }, [navigate, location, params, store])

  // An investments review ends when the user navigates off the screen it belongs to, so the
  // store needs to know where the router is. Ending it on unmount instead would cut it short:
  // reloading investments unmounts and remounts that screen.
  useEffect(() => {
    store.syncInvestmentsReviewPath(location.pathname)
  }, [location.pathname, store])

  const { isAuthenticated } = store.auth

  return (
    <Provider store={store}>
      {/* Only load the org theme with plan access to avoid a 403 logout loop. */}
      <ThemeWrapper isAuth={isAuthenticated && store.hasPlanAccess}>
        <ScrollToTop>
          <Routes>
            <Route path='/implement/guide/:caseId' element={<Guide />} />
            <Route path='/atmos/*' element={<LoginDashboard />} />
            <Route path='/atmos-login' element={<AtmosLogin />} />
            {/* old atmos login that still needs to be here */}
            <Route path='/retail/*' element={<RetailDashboard />} />
            <Route path='*' element={<AppGateway />} />
          </Routes>
        </ScrollToTop>
        <ToastBar />
      </ThemeWrapper>
    </Provider>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  )
}

export default App
